import { RemoteCompany } from "../../../../hooks/Remote/Companies/useRemoteFetchCompanies";
import { CompanyUnits } from "../../../../hooks/Remote/CompanyUnits/useRemoteFetchCompanyUnits";
import { CreateOrUpdateUserDto } from "../../../../hooks/Remote/User/useRemoteCreateOrUpdateUser";
import { UserManagementRow, UserManagementRowValues } from "../typings/user-management.type";
import { ChangeEvent } from "react";
import jsPDF from "jspdf";
import autoTable, { RowInput } from "jspdf-autotable";
import { MRT_Row } from "material-react-table";
import { TFunction } from "i18next";

export function mapAddRowValueToUserDto(values: UserManagementRowValues): CreateOrUpdateUserDto {
  if (values.departmentId === "noDepartment" || values.departmentId === "-1") {
    values.departmentId = null;
  }

  return {
    companyId: Number(values.companyId),
    userEmail: values.email,
    username: `${values.firstName.slice(0, 1)}.${values.surname}`,
    firstName: values.firstName,
    lastName: values.surname,
    isActive: values.isActive,
    activateFirstAid: values.isFirstAidUser ?? false,
    employeeResponsibleEmail: values.supervisorInfo,
    departmentId: values.departmentId,
    participatesInMobileWorking: values.participatesInMobileWorking ?? false
  };
}

export function mapUpdRowValueToUserDto(
  values: UserManagementRowValues,
  rowOriginal: UserManagementRow
): CreateOrUpdateUserDto {
  if (values.departmentId === "noDepartment" || values.departmentId === "-1") {
    values.departmentId = null;
  }

  return {
    userId: rowOriginal.userId,
    companyId: Number(values.companyId ?? rowOriginal.companyId),
    userEmail: values.email ?? rowOriginal.email,
    username: values.username,
    firstName: values.firstName ?? rowOriginal.firstName,
    lastName: values.surname ?? rowOriginal.surname,
    isActive: values.isActive,
    activateFirstAid: values.isFirstAidUser,
    employeeResponsibleEmail: values.supervisorInfo ?? rowOriginal.supervisorInfo,
    departmentId: values.departmentId,
    participatesInMobileWorking:
      values.participatesInMobileWorking ?? rowOriginal.participatesInMobileWorking
  };
}

export function handleRowInputChange(
  e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  rowItem: string,
  validationErrors: Record<string, string | undefined>,
  setValidationErrors: (e: Record<string, string | undefined>) => void
) {
  if (e.currentTarget?.value === "" || e.target?.value === "") {
    setValidationErrors({
      ...validationErrors,
      [`${rowItem}`]: undefined
    });
  }
}

export function updateUserCompanyDepartment(
  values: UserManagementRowValues,
  companyData: RemoteCompany[] | undefined,
  departmentData: CompanyUnits[] | undefined
): UserManagementRowValues {
  const companyId = companyData?.find(
    company => (values.companyIdAndName as string) === company.name + company.id.toString()
  )?.id;

  const departmentId = departmentData?.find(
    department => (values.departmentIdAndName as string) === department.name + department.id
  )?.id;

  return {
    ...values,
    companyId: companyId,
    departmentId: departmentId ?? "noDepartment"
  };
}

export const handleExportRows = (rows: MRT_Row<UserManagementRow>[], t: TFunction) => {
  const doc = new jsPDF({ orientation: "landscape" });

  const tableHeader = [
    t("First Name"),
    t("Last Name"),
    t("Email"),
    t("Supervisor"),
    t("Company"),
    t("Department"),
    t("Status"),
    t("First Aid User"),
    t("Mobile Working")
  ];

  const pdfTableData: RowInput[] = rows.map(({ original }) => [
    original.firstName,
    original.surname,
    original.email,
    original.supervisorInfo,
    original.companyName,
    original.departmentName ?? "",
    original.isActive ? t("ACTIVE") : t("INACTIVE"),
    original.isFirstAidUser ? t("Yes") : t("No"),
    original.participatesInMobileWorking ? t("Yes") : t("No")
  ]) as unknown as RowInput[];

  autoTable(doc, {
    head: [tableHeader],
    body: pdfTableData,
    rowPageBreak: "avoid",
    theme: "striped",
    columnStyles: { 0: { minCellWidth: 15 } }
  });

  doc.save(`${t("Users")}.pdf`);
};
